import type { TocItem } from '@dcxwam/dcx-wam-ui-foundations'
import type { Article } from '../../../../services/faqs/faqs-sdk'
import { accentsTidy } from '../../StoreDetails/utils'
import { buildArticleUrl } from './FaqsUtils'

interface FaqPageQuestion {
  '@type': 'Question'
  name: string
  acceptedAnswer: {
    '@type': 'Answer'
    text: string
  }
}

export interface FaqPageJsonLD {
  '@context': 'https://schema.org'
  '@type': 'FAQPage'
  mainEntity: FaqPageQuestion[]
}

export interface FaqQuestionInput {
  question: string
  answer: string
}

/**
 * Decodes common HTML entities to their character equivalents.
 */
const NAMED_ENTITIES: [string, string][] = [
  ['&amp;', '&'],
  ['&lt;', '<'],
  ['&gt;', '>'],
  ['&quot;', '"'],
  ['&#39;', "'"],
  ['&apos;', "'"]
]

const decodeNumericEntities = (text: string): string => {
  const parts: string[] = []
  let cursor = 0
  let idx = text.indexOf('&#', cursor)

  while (idx !== -1) {
    const semi = text.indexOf(';', idx)
    if (semi === -1) {
      break
    }

    parts.push(text.slice(cursor, idx))
    const raw = text.slice(idx + 2, semi)
    const codePoint =
      raw.startsWith('x') || raw.startsWith('X')
        ? Number.parseInt(raw.slice(1), 16)
        : Number.parseInt(raw, 10)

    parts.push(
      Number.isNaN(codePoint)
        ? text.slice(idx, semi + 1)
        : String.fromCodePoint(codePoint)
    )
    cursor = semi + 1
    idx = text.indexOf('&#', cursor)
  }

  parts.push(text.slice(cursor))
  return parts.join('')
}

const decodeHtmlEntities = (text: string): string =>
  decodeNumericEntities(
    NAMED_ENTITIES.reduce(
      (acc, [entity, char]) => acc.split(entity).join(char),
      text
    )
  )

/**
 * Strips HTML tags from a string and normalizes whitespace.
 * Also decodes common HTML entities.
 */
export const stripHtml = (html: string): string => {
  const result = html
    .split('<')
    .map((segment) => {
      const closingIndex = segment.indexOf('>')
      return closingIndex >= 0 ? segment.slice(closingIndex + 1) : segment
    })
    .join('')
  const normalized = result.split(/\s+/).filter(Boolean).join(' ')
  return decodeHtmlEntities(normalized)
}

export const extractFirstParagraph = (html: string): string => {
  if (!html) {
    return ''
  }
  const paragraphMatch = /<p[^>]*>([\s\S]*?)<\/p>/i.exec(html)
  const firstParagraph = paragraphMatch ? stripHtml(paragraphMatch[1]) : ''
  return firstParagraph || stripHtml(html)
}

/**
 * Extracts Q&A pairs from article HTML content using regex (SSR-safe, no DOMParser).
 * Headings (h1-h6) are treated as questions.
 * All content between one heading and the next is the answer (HTML stripped).
 * Headings without following content get an empty string answer.
 * If no headings exist, falls back to articleTitle as question and full text as answer.
 */
export const extractQuestionsFromHtml = (
  html: string,
  articleTitle?: string
): FaqQuestionInput[] => {
  const headingRegex = /<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/gi
  const questions: FaqQuestionInput[] = []

  const headings: { question: string; endIndex: number; matchStart: number }[] =
    []

  let match = headingRegex.exec(html)
  while (match !== null) {
    const question = stripHtml(match[1])
    if (question) {
      headings.push({
        question,
        endIndex: match.index + match[0].length,
        matchStart: match.index
      })
    }
    match = headingRegex.exec(html)
  }

  // Fallback: no headings found — use articleTitle as question, full text as answer
  if (headings.length === 0) {
    if (articleTitle) {
      const answer = stripHtml(html)
      questions.push({ question: articleTitle, answer: answer || '' })
    }
    return questions
  }

  headings.forEach((heading, i) => {
    const contentStart = heading.endIndex
    const contentEnd =
      i + 1 < headings.length ? headings[i + 1].matchStart : html.length
    const answerHtml = html.slice(contentStart, contentEnd)
    const answer = stripHtml(answerHtml)

    questions.push({ question: heading.question, answer: answer || '' })
  })

  return questions
}

/**
 * Generates a FAQPage JSON-LD schema object from an array of Q&A pairs.
 * Returns null if no valid Q&A pairs are provided.
 */
export const generateFaqPageJsonLD = (
  items: FaqQuestionInput[]
): FaqPageJsonLD | null => {
  const validItems = items.filter((item) => item.question.trim())

  if (validItems.length === 0) {
    return null
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  }
}

export interface ItemListItem {
  name: string
  url: string
}

export interface ItemListJsonLD {
  '@context': 'https://schema.org'
  '@type': 'ItemList'
  name: string
  description?: string
  url: string
  numberOfItems: number
  itemListElement: {
    '@type': 'ListItem'
    position: number
    name: string
    url: string
  }[]
}

export interface ItemListInput {
  name: string
  description?: string
  url: string
  items: ItemListItem[]
}

/**
 * Generates an ItemList JSON-LD schema object for FAQ category pages.
 * Returns null if no valid items are provided.
 */
export const generateItemListJsonLD = (
  input: ItemListInput
): ItemListJsonLD | null => {
  const validItems = input.items.filter(
    (item) => item.name.trim() && item.url.trim()
  )

  if (validItems.length === 0) {
    return null
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: input.name,
    ...(input.description && { description: input.description }),
    url: input.url,
    numberOfItems: validItems.length,
    itemListElement: validItems.map((item, index) => ({
      '@type': 'ListItem' as const,
      position: index + 1,
      name: item.name,
      url: item.url
    }))
  }
}

export interface BuildFaqCollectionListItemsOptions {
  articles: Article[]
  faqArticlePagePath: string
  categorySlug: string
  baseOrigin: string
}

export const buildFaqCollectionListItems = (
  options: BuildFaqCollectionListItemsOptions
): ItemListItem[] => {
  const { articles, faqArticlePagePath, categorySlug, baseOrigin } = options
  return articles.map((article) => {
    const articleSlugValue = `${accentsTidy(article.articleTitle)}-${article.articleId}`
    const relativePath = buildArticleUrl(
      faqArticlePagePath,
      `${categorySlug}/${articleSlugValue}`
    )
    const url = relativePath.startsWith('http')
      ? relativePath
      : `${baseOrigin}${relativePath}`
    return { name: article.articleTitle, url }
  })
}

export const extractTableOfContents = (html: string): TocItem[] => {
  const headingRegex =
    /<h[1-6][^>]*\bid=["']([^"']+)["'][^>]*>([\s\S]*?)<\/h[1-6]>/gi
  const tocItems: TocItem[] = []

  const headings: { anchorId: string; rawLabel: string }[] = []

  let match = headingRegex.exec(html)
  while (match !== null) {
    headings.push({ anchorId: match[1].trim(), rawLabel: match[2] })
    match = headingRegex.exec(html)
  }

  headings.forEach(({ anchorId, rawLabel }) => {
    const label = stripHtml(rawLabel)
    if (anchorId && label) {
      tocItems.push({ label, anchorId })
    }
  })

  return tocItems
}
